import {
  endOfWeek as endOfWeekFns,
  format as formatFns,
  isSameHour,
  parseISO,
  startOfWeek as startOfWeekFns,
} from 'date-fns';
import { format as formatWithTimezone, utcToZonedTime } from 'date-fns-tz';

import 'date-time-format-timezone/build/browserified/date-time-format-timezone-all-zones-no-locale';

import { Locale } from '../_common/utils/locale';

export enum DateStyle {
  FULL = 'full',
  LONG = 'long',
  MEDIUM = 'medium',
  SHORT = 'short',
  NUMERIC = 'numeric',
  DIGIT = '2-digit',
}

export enum DateFormats {
  DAY_OF_WEEK = 'EEEE',
  DAY_OF_MONTH = 'd LLLL',
  DAY = 'd',
  DAY_ORDINAL = 'do',
  MONTH = 'LLLL',
  YEAR = 'y',
  HOURS_MINUTES = 'HH:mm',
  SHORT_DATE = 'yyyy-MM-dd',
}

interface FormatToLocaleDateStringOptions {
  day?: DateStyle;
  hour?: DateStyle;
  minute?: DateStyle;
  month?: DateStyle;
  timezone?: string;
  weekday?: DateStyle;
  year?: DateStyle;
}

export function getStartOfWeek(date: Date = new Date()) {
  return startOfWeekFns(date, { weekStartsOn: 1 });
}

export function getEndOfWeek(date: Date = new Date()) {
  return endOfWeekFns(date, { weekStartsOn: 1 });
}

export function formatTime(date: Date | string, timezone?: string | null) {
  const parsedDate = date instanceof Date ? date : parseISO(date);

  if (timezone != null) {
    return formatWithTimezone(
      utcToZonedTime(parsedDate, timezone),
      DateFormats.HOURS_MINUTES,
      {
        timeZone: timezone,
      },
    );
  }

  return formatFns(parsedDate, DateFormats.HOURS_MINUTES);
}

export function formatToLocaleDateString(
  date: Date | string,
  locale: Locale | string,
  options: FormatToLocaleDateStringOptions,
) {
  const parsedDate = date instanceof Date ? date : parseISO(date);
  const usedDate =
    options.timezone != null
      ? utcToZonedTime(parsedDate, options.timezone)
      : parsedDate;

  return new Intl.DateTimeFormat(locale, options).format(usedDate);
}

export function isDifferentTimezone(customerTimezone?: string | null) {
  if (customerTimezone == null) {
    return false;
  }

  const date = new Date();
  const browserTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone ??
    process.env.DEFAULT_TIMEZONE;

  return !isSameHour(
    utcToZonedTime(date, browserTimezone),
    utcToZonedTime(date, customerTimezone),
  );
}
